import { useCallback, useEffect, useRef, useState } from "react";

export interface UseFormLeaveProtectionReturn {
  modalOpen: boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
}

export function useFormLeaveProtection(
  methods: { formState: { isDirty: boolean } },
  isSubmitted: boolean,
): UseFormLeaveProtectionReturn {
  const { formState } = methods;
  const [modalOpen, setModalOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const shouldBlock = formState.isDirty && !isSubmitted;

  const requestConfirmation = useCallback(
    function requestConfirmation(action: () => void) {
      if (!shouldBlock) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setModalOpen(true);
    },
    [shouldBlock],
  );

  function confirmNavigation() {
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
    setModalOpen(false);
  }

  function cancelNavigation() {
    pendingActionRef.current = null;
    setModalOpen(false);
  }

  useEffect(function handleBeforeUnload() {
    function handler(e: BeforeUnloadEvent) {
      if (!shouldBlock) return;
      e.preventDefault();
      e.returnValue = "";
    }

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [shouldBlock]);

  useEffect(function interceptHistoryNavigation() {
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;

    function isSamePathNavigation(url: string | URL | null | undefined): boolean {
      if (url == null) return true;
      try {
        const target = new URL(String(url), window.location.href);
        return target.pathname === window.location.pathname;
      } catch {
        return false;
      }
    }

    window.history.pushState = (...args: Parameters<typeof window.history.pushState>) => {
      if (shouldBlock && !isSamePathNavigation(args[2])) {
        requestConfirmation(() => {
          originalPush.apply(window.history, args);
        });
        return;
      }
      originalPush.apply(window.history, args);
    };

    window.history.replaceState = (...args: Parameters<typeof window.history.replaceState>) => {
      if (shouldBlock && !isSamePathNavigation(args[2])) {
        requestConfirmation(() => {
          originalReplace.apply(window.history, args);
        });
        return;
      }
      originalReplace.apply(window.history, args);
    };

    return () => {
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
    };
  }, [shouldBlock, requestConfirmation]);

  return {
    modalOpen,
    confirmNavigation,
    cancelNavigation,
  };
}
